import { TERMINAL_DATA } from '../constants/terminalConstants';
import { SEZIONI } from '../constants/cableConstants';
import { needsInnerStripping, resolveTerminal } from './shared';

export const EW05_MACHINE = {
    id: 'EW-05',
    name: 'EW-05',
    description: 'Multipolar wire stripping machine',
    computeSections: (cable, terminalData = TERMINAL_DATA) => {
        const TWO_PASS_THRESHOLD = 150;
        const SLITTING_THRESHOLD = 300;
        const DEFAULT_SEGMENT_INTERVAL = 100;
        const INNER_RETRACT_FACTOR = 0.3;
        const OUTER_RETRACT_FACTOR = 0.2;
        const OUTER_RETRACT_PERCENT = (OUTER_RETRACT_FACTOR * 100).toFixed(0);
        const INNER_RETRACT_PERCENT = (INNER_RETRACT_FACTOR * 100).toFixed(0);

        const poli = cable.poli || [];
        if (poli.length === 0) return [];

        const lunghezzeA = poli.map(p => p.lunghezzaA || 0);
        const targetStripL = Math.max(...lunghezzeA);

        const lunghezzeB = poli.map(p => p.lunghezzaB || 0);
        const targetStripR = Math.max(...lunghezzeB);

        const needsSplitL = targetStripL > TWO_PASS_THRESHOLD;
        const needsSplitR = targetStripR > TWO_PASS_THRESHOLD;
        const hasTwoPass = needsSplitL || needsSplitR;

        const needsSlittingL = targetStripL > SLITTING_THRESHOLD;
        const needsSlittingR = targetStripR > SLITTING_THRESHOLD;
        const hasSlitting = needsSlittingL || needsSlittingR;

        const innerEnabled = needsInnerStripping(cable, terminalData);

        let innerStripL = 0, innerPullCombineL = 0;
        let innerStripR = 0, innerPullCombineR = 0;

        if (innerEnabled) {
            const maxLunghezzaA = Math.max(...poli.map(p => p.lunghezzaA || 0));
            const maxLunghezzaB = Math.max(...poli.map(p => p.lunghezzaB || 0));

            poli.forEach(polo => {
                const termA = resolveTerminal(polo.terminaleA, terminalData);
                const termB = resolveTerminal(polo.terminaleB, terminalData);

                if ((polo.lunghezzaA || 0) === maxLunghezzaA && termA && termA.peel > 0) {
                    innerStripL = Math.max(innerStripL, termA.len || 0);
                    innerPullCombineL = Math.max(innerPullCombineL, termA.peel || 0);
                }

                if ((polo.lunghezzaB || 0) === maxLunghezzaB && termB && termB.peel > 0) {
                    innerStripR = Math.max(innerStripR, termB.len || 0);
                    innerPullCombineR = Math.max(innerPullCombineR, termB.peel || 0);
                }
            });
        }

        const sezioni = [...new Set(poli.map(p => p.sezione))];
        const maxSezione = Math.max(...sezioni);
        // La sezione è sempre in mm²: il diametro fisico si cerca nella tabella metrica.
        const sezioneEntry = SEZIONI.find(s => s.val === maxSezione)
            || SEZIONI.reduce((prev, curr) =>
                Math.abs(curr.val - maxSezione) < Math.abs(prev.val - maxSezione) ? curr : prev
            );
        const maxDiametro = (sezioneEntry && sezioneEntry.dia !== '') ? sezioneEntry.dia : null;

        const sections = [];

        sections.push({
            id: 'general',
            label: 'Generale',
            labelKey: 'machineConfig.EW05.sections.general',
            parameters: [
                {
                    key: 'fullLength',
                    label: 'Full Length',
                    value: cable.lunghezza,
                    unit: 'mm',
                    tooltipKey: 'machineConfig.EW05.general.lenght'
                },
                {
                    key: 'bladeValue',
                    label: 'Blade Value',
                    value: null,
                    unit: 'mm',
                    manual: true,
                    tooltipKey: 'machineConfig.EW05.general.bladeValue'
                },
                {
                    key: 'retract',
                    label: 'Retract',
                    value: null,
                    unit: 'mm',
                    manual: false,
                    linkedTo: 'bladeValue',
                    factor: OUTER_RETRACT_FACTOR,
                    ceilOneDecimal: true,
                    tooltipKey: 'machineConfig.EW05.general.retractWithPercent',
                    tooltipParams: { percent: OUTER_RETRACT_PERCENT }
                },
                {
                    key: 'rollerLGap',
                    label: 'Roller L. Gap',
                    value: null,
                    unit: 'mm',
                    manual: false,
                    linkedTo: 'bladeValue',
                    factor: 0.7,
                    floorHalf: true,
                    tooltipKey: 'machineConfig.EW05.general.rollerLGap'
                },
                {
                    key: 'rollerRGap',
                    label: 'Roller R. Gap',
                    value: null,
                    unit: 'mm',
                    manual: false,
                    linkedTo: 'bladeValue',
                    factor: 0.7,
                    floorHalf: true,
                    tooltipKey: 'machineConfig.EW05.general.rollerRGap'
                },
            ]
        });

        sections.push({
            id: 'outer1',
            label: 'OUTER',
            sublabel: hasTwoPass ? 'Sguainatura guaina esterna - 1\u00b0 passata' : 'Sguainatura guaina esterna',
            sublabelKey: hasTwoPass
                ? 'machineConfig.EW05.sections.outer1TwoPass'
                : 'machineConfig.EW05.sections.outer1Single',
            parameters: [
                {
                    key: 'stripL',
                    label: 'Strip L',
                    value: needsSplitL ? TWO_PASS_THRESHOLD : targetStripL,
                    unit: 'mm',
                    tooltipKey: needsSplitL
                        ? 'machineConfig.EW05.outer1.stripLMax'
                        : 'machineConfig.EW05.outer1.stripL',
                    tooltipParams: needsSplitL ? { max: TWO_PASS_THRESHOLD } : undefined
                },
                {
                    key: 'pullOffL',
                    label: 'Pull-off',
                    value: needsSplitL ? (TWO_PASS_THRESHOLD + 5) : (targetStripL + 5),
                    unit: 'mm',
                    manual: false,
                    tooltipKey: 'machineConfig.EW05.outer1.pullOffComplete'
                },
                {
                    key: 'stripR',
                    label: 'Strip R',
                    value: needsSplitR ? TWO_PASS_THRESHOLD : targetStripR,
                    unit: 'mm',
                    tooltipKey: needsSplitR
                        ? 'machineConfig.EW05.outer1.stripRMax'
                        : 'machineConfig.EW05.outer1.stripR',
                    tooltipParams: needsSplitR ? { max: TWO_PASS_THRESHOLD } : undefined
                },
                {
                    key: 'pullOffR',
                    label: 'Pull-off',
                    value: needsSplitR ? (TWO_PASS_THRESHOLD + 5) : (targetStripR + 5),
                    unit: 'mm',
                    manual: false,
                    tooltipKey: 'machineConfig.EW05.outer1.pullOffRComplete'
                },
            ]
        });

        sections.push({
            id: 'inner',
            label: 'INNER',
            sublabel: 'Spelatura cavi interni',
            sublabelKey: 'machineConfig.EW05.sections.inner',
            enabled: innerStripL > 0 || innerStripR > 0,
            parameters: innerStripL > 0 || innerStripR > 0 ? [
                {
                    key: 'stripL',
                    label: 'Strip L',
                    value: innerStripL,
                    unit: 'mm',
                    tooltipKey: 'machineConfig.EW05.inner.stripL'
                },
                {
                    key: 'pullOffL',
                    label: 'Pull-off',
                    value: innerPullCombineL,
                    unit: 'mm',
                    tooltipKey: innerPullCombineL >= innerStripL
                        ? 'machineConfig.EW05.inner.pullOffLComplete'
                        : 'machineConfig.EW05.inner.pullOffLPartial'
                },
                {
                    key: 'bladeValue',
                    label: 'Blade Value',
                    value: maxDiametro !== null ? Math.round(Math.max(1, maxDiametro) * 1.1 * 10) / 10 : null,
                    unit: 'mm',
                    tooltipKey: 'machineConfig.EW05.inner.bladeValue'
                },
                {
                    key: 'retract',
                    label: 'Retract',
                    value: maxDiametro !== null
                        ? Math.ceil(Math.max(1, maxDiametro) * INNER_RETRACT_FACTOR * 10) / 10
                        : null,
                    unit: 'mm',
                    tooltipKey: 'machineConfig.EW05.inner.retractWithPercent',
                    tooltipParams: { percent: INNER_RETRACT_PERCENT }
                },
                {
                    key: 'stripR',
                    label: 'Strip R',
                    value: innerStripR,
                    unit: 'mm',
                    tooltipKey: 'machineConfig.EW05.inner.stripR'
                },
                {
                    key: 'pullOffR',
                    label: 'Pull-off',
                    value: innerPullCombineR,
                    unit: 'mm',
                    tooltipKey: innerPullCombineR >= innerStripR
                        ? 'machineConfig.EW05.inner.pullOffRComplete'
                        : 'machineConfig.EW05.inner.pullOffRPartial'
                },
            ] : []
        });

        if (hasTwoPass) {
            const outer2StripL = needsSplitL
                ? (needsSlittingL ? SLITTING_THRESHOLD : targetStripL)
                : 0;
            const outer2StripR = needsSplitR
                ? (needsSlittingR ? SLITTING_THRESHOLD : targetStripR)
                : 0;

            sections.push({
                id: 'outer2',
                label: 'OUTER',
                sublabel: 'Sguainatura guaina esterna - 2\u00b0 passata',
                sublabelKey: 'machineConfig.EW05.sections.outer2',
                parameters: [
                    {
                        key: 'stripL',
                        label: 'Strip L',
                        value: outer2StripL,
                        unit: 'mm',
                        tooltipKey: outer2StripL > 0
                            ? 'machineConfig.EW05.outer2.stripL'
                            : 'machineConfig.EW05.outer2.stripLNone'
                    },
                    {
                        key: 'pullOffL',
                        label: 'Pull-off',
                        value: outer2StripL === 0 ? 0 : (outer2StripL > 200 ? 200 : outer2StripL + 5),
                        unit: 'mm',
                        manual: false,
                        tooltipKey: outer2StripL === 0
                            ? 'machineConfig.EW05.outer2.pullOffLNone'
                            : outer2StripL > 200
                                ? 'machineConfig.EW05.outer2.pullOffLMax'
                                : 'machineConfig.EW05.outer2.pullOffLComplete',
                        tooltipParams: outer2StripL === 0
                            ? undefined
                            : outer2StripL > 200
                                ? { max: 200 }
                                : undefined
                    },
                    {
                        key: 'stripR',
                        label: 'Strip R',
                        value: outer2StripR,
                        unit: 'mm',
                        tooltipKey: outer2StripR > 0
                            ? 'machineConfig.EW05.outer2.stripR'
                            : 'machineConfig.EW05.outer2.stripRNone'
                    },
                    {
                        key: 'pullOffR',
                        label: 'Pull-off',
                        value: outer2StripR === 0 ? 0 : (outer2StripR > 200 ? 200 : outer2StripR + 5),
                        unit: 'mm',
                        manual: false,
                        tooltipKey: outer2StripR === 0
                            ? 'machineConfig.EW05.outer2.pullOffRNone'
                            : outer2StripR > 200
                                ? 'machineConfig.EW05.outer2.pullOffRMax'
                                : 'machineConfig.EW05.outer2.pullOffRComplete',
                        tooltipParams: outer2StripR === 0
                            ? undefined
                            : outer2StripR > 200
                                ? { max: 200 }
                                : undefined
                    },
                ]
            });
        }

        if (hasSlitting) {
            const cableLength = cable.lunghezza;

            const computeUnifiedSlots = (tStripL, tStripR, cLen, segInterval) => {
                const slots = [];

                if (tStripL > SLITTING_THRESHOLD) {
                    let lastLeftPos = SLITTING_THRESHOLD;
                    for (let pos = SLITTING_THRESHOLD + segInterval; pos <= tStripL; pos += segInterval) {
                        slots.push({ startPosition: pos, peelLength: 0.1 });
                        lastLeftPos = pos;
                    }
                    if (lastLeftPos < tStripL) {
                        slots.push({ startPosition: tStripL, peelLength: 0.1 });
                    }
                }

                if (tStripR > SLITTING_THRESHOLD) {
                    for (let pos = cLen - tStripR; pos < cLen - SLITTING_THRESHOLD; pos += segInterval) {
                        slots.push({ startPosition: pos, peelLength: 0.1 });
                    }
                }

                slots.sort((a, b) => a.startPosition - b.startPosition);
                return slots;
            };

            const computeKnifeSlots = (tStripL, tStripR, cLen) => {
                const slots = [];
                if (tStripL > SLITTING_THRESHOLD) {
                    slots.push({ startPosition: SLITTING_THRESHOLD, peelLength: tStripL - SLITTING_THRESHOLD });
                }
                if (tStripR > SLITTING_THRESHOLD) {
                    slots.push({ startPosition: cLen - tStripR, peelLength: tStripR - SLITTING_THRESHOLD });
                }
                slots.sort((a, b) => a.startPosition - b.startPosition);
                return slots;
            };

            const effectiveStripL = needsSlittingL ? targetStripL : 0;
            const effectiveStripR = needsSlittingR ? targetStripR : 0;

            sections.push({
                id: 'slitting',
                label: 'SLITTING',
                sublabel: 'Incisione guaina esterna',
                sublabelKey: 'machineConfig.EW05.sections.slitting',
                type: 'slitting',
                slittingData: {
                    defaultSegmentInterval: DEFAULT_SEGMENT_INTERVAL,
                    cableLength,
                    needsSlittingL,
                    needsSlittingR,
                    targetStripL: effectiveStripL,
                    targetStripR: effectiveStripR,
                    remainingL: needsSlittingL ? targetStripL - SLITTING_THRESHOLD : 0,
                    remainingR: needsSlittingR ? targetStripR - SLITTING_THRESHOLD : 0,
                    unifiedSlots: computeUnifiedSlots(effectiveStripL, effectiveStripR, cableLength, DEFAULT_SEGMENT_INTERVAL),
                    knifeSlots: computeKnifeSlots(effectiveStripL, effectiveStripR, cableLength),
                }
            });
        }

        return sections;
    }
};
